import { addCrumbs, getCollection } from '../core/save.js';
import { addFound } from '../core/collection.js';
import { getHour } from '../core/world.js';
import { getWeather, getSeason } from '../core/weather.js';
import { advanceTime } from '../core/world.js';
import { progress, flagObjective } from '../core/quests.js';
import { spendForAction } from '../core/actions.js';
import { HUD } from '../ui/hud.js';

// Fishing minigame for the Toast Town beach.
// 1. Cast and wait for a bite.
// 2. Tap/click to hook when the exclamation mark appears.
// 3. Hold to reel while keeping tension in the green zone.
// Energy cost is paid once at the start via the action budget.

const FISH_TABLE = [
  { id: 'fish-toastfish',   weight: 30, weather: ['clear','cloudy','windy'], seasons: ['spring','summer','autumn','winter'] },
  { id: 'fish-breadbass',   weight: 25, weather: ['clear','cloudy','rain'],  seasons: ['spring','summer','autumn','winter'] },
  { id: 'fish-crumbsalmon', weight: 15, weather: ['rain','cloudy'],          seasons: ['spring','autumn'] },
  { id: 'fish-jellyjelly',  weight: 12, weather: ['clear','cloudy'],         seasons: ['summer'] },
  { id: 'fish-lobsterroll', weight: 8,  weather: ['rain','windy'],           seasons: ['spring','autumn','winter'] },
  { id: 'fish-goldencrumb', weight: 5,  weather: ['clear'],                  seasons: ['summer','autumn'] },
  { id: 'fish-whalebread',  weight: 2,  weather: ['clear','rain'],           seasons: ['summer','winter'] },
  { id: 'fish-bootfish',    weight: 18, weather: ['rain','windy','snow'],    seasons: ['spring','summer','autumn','winter'] }
];

export class FishingGame {
  constructor(){
    this.root = null;
    this.state = 'idle'; // idle -> waiting -> bite -> reeling -> done
    this.onDone = null;
    this.time = 0;
    this.biteAt = 0;
    this.biteWindow = 0;
    this.tension = 0.5;
    this.fishPull = 0;
    this.reelProgress = 0;
    this.reeling = false;
    this.fishId = null;
    this.result = null;
  }

  mount(root){
    this.root = root;
    this.root.innerHTML = `
      <div class="fish-hud">
        <div class="fish-rod">
          <div class="fish-line"></div>
          <div class="fish-bobber">🎣</div>
          <div class="fish-bite" hidden>!</div>
        </div>
        <div class="fish-info">
          <div class="fish-status">tap the water to cast</div>
          <div class="fish-help">wait for a bite, then tap to hook. hold to reel, but keep the tension in the green.</div>
        </div>
        <div class="fish-tension" hidden>
          <div class="fish-zone"></div>
          <div class="fish-marker"></div>
        </div>
        <button class="fish-quit">leave rod</button>
      </div>`;
    this.statusEl = this.root.querySelector('.fish-status');
    this.bobberEl = this.root.querySelector('.fish-bobber');
    this.biteEl = this.root.querySelector('.fish-bite');
    this.tensionEl = this.root.querySelector('.fish-tension');
    this.markerEl = this.root.querySelector('.fish-marker');
    this.zoneEl = this.root.querySelector('.fish-zone');
    this.helpEl = this.root.querySelector('.fish-help');

    this.root.querySelector('.fish-quit').onclick = () => this._finish(false);
    this.root.querySelector('.fish-rod').onclick = () => this._tap();
    this.root.onmousedown = () => { this.reeling = true; };
    this.root.onmouseup = () => { this.reeling = false; };
    this.root.ontouchstart = (e) => { e.preventDefault(); this.reeling = true; };
    this.root.ontouchend = (e) => { e.preventDefault(); this.reeling = false; };
  }

  begin(){
    const check = spendForAction('fish');
    if (!check.ok){
      HUD.say(check.reason || 'You are too tired to fish.');
      this._finish(false);
      return;
    }
    this.state = 'idle';
    this.time = 0;
    this.reelProgress = 0;
    this.tension = 0.5;
    this.fishPull = 0.4;
    this.fishId = this._pickFish();
  }

  _pickFish(){
    const weather = getWeather();
    const season = getSeason();
    const hour = getHour();
    const pool = [];
    for (const f of FISH_TABLE){
      if (!f.weather.includes(weather) && !f.seasons.includes(season)) continue;
      let w = f.weight;
      if (f.weather.includes(weather)) w *= 1.5;
      if (f.seasons.includes(season)) w *= 1.5;
      if (hour >= 18 || hour < 5) w *= 0.8; // night fishing is a bit harder
      for (let i = 0; i < Math.max(1, Math.round(w)); i++) pool.push(f.id);
    }
    if (pool.length === 0) return 'fish-bootfish';
    return pool[(Math.random() * pool.length) | 0];
  }

  _tap(){
    if (this.state === 'idle'){
      this.state = 'waiting';
      this.biteAt = 1.5 + Math.random() * 3;
      this.biteWindow = 0.8 + Math.random() * 0.6;
      this.statusEl.textContent = 'waiting for a bite...';
      this.helpEl.textContent = 'watch the bobber! tap when you see "!"';
      this.bobberEl.textContent = '🎣';
      return;
    }
    if (this.state === 'bite'){
      this.state = 'reeling';
      this.biteEl.hidden = true;
      this.tensionEl.hidden = false;
      this.statusEl.textContent = 'reel it in!';
      this.helpEl.textContent = 'hold to reel. keep the marker in the green zone.';
      this.fishPull = 0.35 + Math.random() * 0.3;
      return;
    }
  }

  update(dt){
    if (this.state === 'waiting'){
      this.time += dt;
      this.bobberEl.style.transform = `translateY(${Math.sin(this.time * 4) * 4}px)`;
      if (this.time >= this.biteAt){
        this.state = 'bite';
        this.biteEl.hidden = false;
      }
      if (this.time > this.biteAt + this.biteWindow){
        this.statusEl.textContent = 'it got away...';
        this.biteEl.hidden = true;
        this._finish(false);
      }
    } else if (this.state === 'bite'){
      this.time += dt;
      if (this.time > this.biteAt + this.biteWindow){
        this.statusEl.textContent = 'it got away...';
        this.biteEl.hidden = true;
        this._finish(false);
      }
    } else if (this.state === 'reeling'){
      // tension drifts toward fish pull; reeling pulls it down, fish pulls up
      const pull = this.fishPull + Math.sin(performance.now() * 0.004) * 0.15;
      const reel = this.reeling ? -0.55 : 0.25;
      this.tension = Math.max(0, Math.min(1, this.tension + (pull + reel) * dt));
      this.reelProgress += this.reeling ? dt * 0.18 : 0;
      this._updateMarker();
      if (this.tension >= 1){
        this.statusEl.textContent = 'line snapped!';
        this._finish(false);
      } else if (this.reelProgress >= 1){
        this.statusEl.textContent = 'caught one!';
        this._finish(true);
      }
    }
  }

  _updateMarker(){
    this.markerEl.style.left = `${this.tension * 100}%`;
    const inGreen = this.tension >= 0.35 && this.tension <= 0.65;
    this.zoneEl.style.background = inGreen ? 'rgba(127,191,106,.55)' : 'rgba(244,155,51,.35)';
  }

  _finish(caught){
    if (this.result) return;
    this.result = caught ? 'caught' : 'lost';
    this.tensionEl.hidden = true;
    this.biteEl.hidden = true;
    if (caught){
      const rarity = { common: 5, uncommon: 12, rare: 30, legendary: 75 };
      const value = rarity[this._rarity(this.fishId)] || 5;
      addCrumbs(value);
      addFound(this.fishId);
      progress('fishCaught', 1);
      flagObjective('first-catch', 'caughtFirstFish');
      this._checkDonations();
      HUD.say(`Caught a ${this._fishName(this.fishId)}! +${value} crumbs`);
      HUD.refreshCrumbs();
    } else {
      HUD.say('The fish got away.');
    }
    setTimeout(() => this.onDone?.(this.result), 800);
  }

  _rarity(id){
    // mirror collection rarities for payout
    const r = {
      'fish-toastfish':'common','fish-breadbass':'common','fish-bootfish':'common',
      'fish-crumbsalmon':'uncommon','fish-jellyjelly':'uncommon',
      'fish-lobsterroll':'rare','fish-goldencrumb':'rare',
      'fish-whalebread':'legendary'
    };
    return r[id] || 'common';
  }

  _checkDonations(){
    const found = getCollection().found;
    const distinct = Object.keys(found).filter(id => id.startsWith('fish-') && found[id] > 0).length;
    if (distinct >= 3) flagObjective('aquarium-donate', 'donatedThreeFish');
  }

  _fishName(id){
    const names = {
      'fish-toastfish':'Toastfish','fish-breadbass':'Bread Bass','fish-crumbsalmon':'Crumb Salmon',
      'fish-jellyjelly':'Jelly Jelly','fish-lobsterroll':'Lobster Roll','fish-goldencrumb':'Golden Crumbfish',
      'fish-whalebread':'Whalebread','fish-bootfish':'Bootfish'
    };
    return names[id] || 'fish';
  }

  unmount(){
    this.root.innerHTML = '';
    this.root = null;
  }
}
